import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircle, CheckCircle } from 'lucide-react';
import ChatContent from './ChatContent';
import LocationDetailsStep from './LocationDetailsStep';
import OfferOverviewStep from './OfferOverviewStep';
import ContactFormStep from './ContactFormStep';
import { getServiceLineGross, formatOfferTotalText } from '../utils/offerLineDisplay';

// Darken a hex color by a factor 0–1 (e.g. 0.15 = 15% darker)
function darkenHex(hex, factor = 0.15) {
  const match = hex.replace('#', '').match(hex.length <= 4 ? /^(.)(.)(.)$/ : /^(.{2})(.{2})(.{2})$/);
  if (!match) return hex;
  const toNum = (s) => (s.length === 1 ? parseInt(s + s, 16) : parseInt(s, 16));
  const r = Math.max(0, Math.round(toNum(match[1]) * (1 - factor)));
  const g = Math.max(0, Math.round(toNum(match[2]) * (1 - factor)));
  const b = Math.max(0, Math.round(toNum(match[3]) * (1 - factor)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

const StepperChatbotWidget = ({ showIntroStep = true }) => {
  const STEPS = showIntroStep ? ['intro', 'chat', 'locationDetails', 'offerOverview', 'contact', 'confirmation'] : ['chat', 'locationDetails', 'offerOverview', 'contact', 'confirmation'];
  const { t } = useTranslation();
  const { chatbotId } = useParams();
  const [chatbot, setChatbot] = useState(null);
  const [currentStep, setCurrentStep] = useState(showIntroStep ? 'intro' : 'chat');
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversationEnded, setConversationEnded] = useState(false);
  const [hasDocument, setHasDocument] = useState(false);
  const [offerId, setOfferId] = useState(null);
  const [offerToken, setOfferToken] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [pendingOffer, setPendingOffer] = useState(null);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlBg = html.style.backgroundColor;
    const prevBodyBg = body.style.backgroundColor;
    const prevMinHeight = body.style.minHeight;
    html.style.backgroundColor = 'transparent';
    body.style.backgroundColor = 'transparent';
    body.style.minHeight = '800px';
    return () => {
      html.style.backgroundColor = prevHtmlBg;
      body.style.backgroundColor = prevBodyBg;
      body.style.minHeight = prevMinHeight;
    };
  }, []);

  useEffect(() => {
    fetchChatbotConfig();
  }, [chatbotId]);

  const fetchChatbotConfig = async () => {
    try {
      const response = await fetch(`/api/embed/${chatbotId}/config?t=${Date.now()}`);
      if (response.ok) {
        const data = await response.json();
        setChatbot(data);
        const hasServices = data.services && Array.isArray(data.services) && data.services.length > 0;
        setHasDocument(hasServices);

        let greetingMessage;
        if (!hasServices) {
          greetingMessage = data.companyName
            ? t('embed.noDocWithCompany', { company: data.companyName })
            : t('embed.noDocNoCompany');
        } else if (data.greeting && data.greeting.trim()) {
          const company = data.companyName || '';
          greetingMessage = data.greeting.trim().replace(/\{\{companyName\}\}/g, company).replace(/\{\{BETRIEBNAME\}\}/g, company);
        } else {
          greetingMessage = data.companyName
            ? t('embed.greetingWithCompany', { company: data.companyName })
            : t('embed.greetingNoCompany');
        }

        setMessages([{ role: 'assistant', content: greetingMessage }]);
      } else {
        throw new Error('Chatbot not found');
      }
    } catch (error) {
      console.error('Error loading chatbot:', error);
      setMessages([{ role: 'assistant', content: t('embed.loadError') }]);
      setHasDocument(false);
    }
  };

  const extractOfferDetails = (structuredResponse) => {
    if (!structuredResponse || !structuredResponse.offer) return null;
    const offer = structuredResponse.offer;
    let offerDetails = structuredResponse.content || '';

    if (offer.services && offer.services.length > 0) {
      offerDetails += '\n\nServices included:\n';
      offer.services.forEach((service, index) => {
        offerDetails += `${index + 1}. ${service.name}`;
        if (service.description) offerDetails += ` - ${service.description}`;
        if (service.price !== undefined) {
          offerDetails += ` (${offer.currency || 'EUR'} ${getServiceLineGross(service).toFixed(2)} brutto)`;
        }
        offerDetails += '\n';
      });
    }

    const totalAmount = formatOfferTotalText(offer) || 'TBD';

    return {
      offerDetails,
      totalAmount,
      services: offer.services || [],
      currency: offer.currency || 'EUR',
      validUntil: offer.validUntil || null,
      notes: offer.notes || '',
      unlistedItems: Array.isArray(offer.unlistedItems) ? offer.unlistedItems : [],
      delivery: offer.delivery === true,
      sharedSelections: Array.isArray(offer.sharedSelections) ? offer.sharedSelections : [],
      displayMode: offer.displayMode,
      priceRange: offer.priceRange,
      createdAt: new Date()
    };
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    if (!hasDocument) {
      setMessages(prev => [...prev, { role: 'assistant', content: t('embed.noDocCannotStart') }]);
      return;
    }

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`/api/embed/${chatbotId}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage, conversationId }),
      });

      const data = await response.json();
      const currentConversationId = data.conversationId;
      setConversationId(currentConversationId);

      const responseType = data.type || 'message';
      const responseContent = data.response || '';
      const offerData = data.offer || null;

      if (responseType === 'offer' && offerData) {
        const structuredResponse = { type: responseType, content: responseContent, offer: offerData };
        const offer = extractOfferDetails(structuredResponse);
        if (offer) {
          if (!chatbot?.developerMode) {
            setConversationEnded(true);
          }
          setOfferId(null);
          setOfferToken(null);
          setPendingOffer({
            ...offer,
            conversationId: currentConversationId || conversationId,
          });
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: t('stepper.provideLocationDetails')
          }]);
          setCurrentStep('locationDetails');
        }
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: responseContent }]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = error.message.includes('price list') || error.message.includes('No price list')
        ? t('embed.noDocNoCompany')
        : t('embed.errorOccurred');
      setMessages(prev => [...prev, { role: 'assistant', content: errorMessage }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!conversationEnded) sendMessage();
    }
  };

  const handleOfferLinkClick = () => {
    setCurrentStep(pendingOffer ? 'locationDetails' : 'offerOverview');
  };

  /** Developer mode: return to chat from post-offer steps; drop persisted offer id so a new flow can start. */
  const handleDeveloperBackToOfferFlow = () => {
    setCurrentStep('chat');
    setOfferId(null);
    setOfferToken(null);
  };

  const handleLocationDetailsSubmit = async (locationData) => {
    if (!pendingOffer) return;
    try {
      const offerResponse = await fetch('/api/offers/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId: pendingOffer.conversationId,
          offerDetails: pendingOffer.offerDetails,
          customerInfo: {
            locationZip: locationData.zip,
            parkPlaces: locationData.parkPlaces,
            floor: locationData.floor,
          },
          services: pendingOffer.services,
          totalAmount: pendingOffer.totalAmount,
          chatbotId: chatbotId,
          unlistedItems: pendingOffer.unlistedItems || [],
          delivery: pendingOffer.delivery === true,
          sharedSelections: pendingOffer.sharedSelections || []
        }),
      });

      if (!offerResponse.ok) throw new Error('Failed to create offer');

      const offerResult = await offerResponse.json();
      setOfferId(offerResult.offerId);
      setOfferToken(offerResult.accessToken);
      setPendingOffer(null);
      setCurrentStep('offerOverview');
    } catch (error) {
      console.error('Error creating offer:', error);
      alert(t('embed.offerCreateError'));
    }
  };

  if (!chatbot) {
    return (
      <div className="w-full max-w-[1152px] mx-auto bg-white rounded-lg shadow-xl overflow-hidden flex items-center justify-center" style={{ width: '100%', minWidth: '320px', height: '800px' }}>
        <p className="text-gray-600">{t('embed.loadingChatbot')}</p>
      </div>
    );
  }

  const primaryColor = chatbot?.primaryColor || '#3b82f6';
  const primaryColorHover = darkenHex(primaryColor, 0.15);
  const steps = STEPS;
  const currentStepIndex = steps.indexOf(currentStep);

  return (
    <div className="w-full max-w-[1152px] mx-auto bg-white rounded-lg shadow-xl overflow-hidden flex flex-col" style={{ width: '100%', minWidth: '320px', height: '800px' }}>
      {/* Step indicator */}
      <div
        className="flex items-center justify-center gap-2 sm:gap-4 px-4 py-3 border-b border-gray-200"
        style={{ backgroundColor: `${primaryColor}10` }}
      >
        {steps.map((step, index) => (
          <React.Fragment key={step}>
            <div className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  index <= currentStepIndex
                    ? 'text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
                style={index <= currentStepIndex ? { backgroundColor: primaryColor } : undefined}
              >
                {index + 1}
              </div>
              <span className={`text-sm font-medium hidden sm:inline ${
                index <= currentStepIndex ? 'text-gray-900' : 'text-gray-500'
              }`}>
                {t(`stepper.step${step.charAt(0).toUpperCase() + step.slice(1)}`)}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div className="w-8 h-0.5 bg-gray-300 mx-1" aria-hidden="true" />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step content */}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        {currentStep === 'intro' && (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <MessageCircle size={32} style={{ color: primaryColor }} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('stepper.introTitle')}</h2>
            <p className="text-gray-600 mb-6 max-w-md">{t('stepper.introDescription')}</p>
            <button
              onClick={() => setCurrentStep('chat')}
              className="px-8 py-3 text-white rounded-lg font-medium transition-colors"
              style={{ backgroundColor: primaryColor }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = primaryColorHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = primaryColor; }}
            >
              {t('stepper.startButton')}
            </button>
          </div>
        )}

        {currentStep === 'chat' && (
          <div className="flex flex-col flex-1 min-h-0">
            <div
              className="px-4 py-3 border-b border-gray-200 flex items-center gap-3"
              style={{ backgroundColor: primaryColor }}
            >
              <div className="w-8 h-8 flex-shrink-0 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <MessageCircle size={16} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-white">{chatbot.name}</h3>
                <p className="text-xs text-white opacity-90">{chatbot.companyName}</p>
              </div>
            </div>
            <ChatContent
              messages={messages}
              inputMessage={inputMessage}
              onInputChange={setInputMessage}
              onSendMessage={sendMessage}
              onKeyPress={handleKeyPress}
              isLoading={isLoading}
              conversationEnded={conversationEnded}
              hasDocument={hasDocument}
              primaryColor={primaryColor}
              primaryColorHover={primaryColorHover}
              maxWidthClass="sm:max-w-[360px]"
              onOfferLinkClick={handleOfferLinkClick}
            />
          </div>
        )}

        {currentStep === 'locationDetails' && pendingOffer && (
          <div className="flex-1 overflow-y-auto min-h-0">
            <LocationDetailsStep
              onContinue={handleLocationDetailsSubmit}
              onBack={() => setCurrentStep('chat')}
              compact={true}
              showBackButton={!!chatbot?.developerMode}
              primaryColor={primaryColor}
            />
          </div>
        )}

        {currentStep === 'offerOverview' && offerId && offerToken && (
          <div className="flex-1 overflow-y-auto min-h-0">
            <OfferOverviewStep
              offerId={offerId}
              token={offerToken}
              onContinue={() => setCurrentStep('contact')}
              onReject={() => setCurrentStep('chat')}
              compact={true}
              showRejectButton={false}
              showBackToChat={!!chatbot?.developerMode}
              onBackToChat={handleDeveloperBackToOfferFlow}
            />
          </div>
        )}

        {currentStep === 'contact' && offerId && offerToken && (
          <div className="flex-1 overflow-y-auto min-h-0">
            <ContactFormStep
              offerId={offerId}
              token={offerToken}
              onSuccess={(info) => {
                setCustomerInfo(info);
                setCurrentStep('confirmation');
              }}
              onReject={() => setCurrentStep('chat')}
              compact={true}
              showRejectButton={false}
              showBackToChat={!!chatbot?.developerMode}
              onBackToChat={handleDeveloperBackToOfferFlow}
            />
          </div>
        )}

        {currentStep === 'confirmation' && (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('offer.offerSentSuccess')}</h3>
            <p className="text-gray-600 mb-6 whitespace-pre-line text-left max-w-lg mx-auto">
              {t('offer.submissionNotice', { email: customerInfo?.email || '' })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StepperChatbotWidget;
